import { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  NumberInput,
  Paper,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useProjectCostCodes,
  useAssignCostCodes,
  useRemoveProjectCostCode,
  useUpdateProjectCostCode,
} from "@/hooks/useCostCodes";
import { CostCodeSelector } from "@/components/CostCodeSelector";

export const Route = createFileRoute("/projects/$projectId/cost-codes" as any)({
  component: ProjectCostCodesPage,
});

function ProjectCostCodesPage() {
  const params = Route.useParams();
  const projectId = "projectId" in params ? params.projectId : "";
  const { data, isLoading } = useProjectCostCodes(projectId);
  const assignCostCodes = useAssignCostCodes(projectId);
  const removeProjectCostCode = useRemoveProjectCostCode(projectId);
  const updateProjectCostCode = useUpdateProjectCostCode(projectId);
  const [selectorOpened, { open: openSelector, close: closeSelector }] = useDisclosure(false);
  const [editingBudget, setEditingBudget] = useState<string | null>(null);

  const projectCostCodes = data?.projectCostCodes || [];

  const handleAddCostCodes = async (costCodeIds: string[]) => {
    try {
      await assignCostCodes.mutateAsync({ costCodeIds });
      notifications.show({
        title: "Success",
        message: "Cost codes added to project!",
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to add cost codes",
        color: "red",
      });
    }
  };

  const handleRemove = async (projectCostCodeId: string) => {
    try {
      await removeProjectCostCode.mutateAsync(projectCostCodeId);
      notifications.show({
        title: "Success",
        message: "Cost code removed from project!",
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to remove cost code",
        color: "red",
      });
    }
  };

  const handleBudgetUpdate = async (projectCostCodeId: string, budgetAmount: number | string) => {
    try {
      await updateProjectCostCode.mutateAsync({
        projectCostCodeId,
        data: {
          budgetAmount: budgetAmount === "" ? undefined : Number(budgetAmount),
        },
      });
      notifications.show({
        title: "Success",
        message: "Budget updated!",
        color: "green",
      });
      setEditingBudget(null);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to update budget",
        color: "red",
      });
    }
  };

  // Calculate total budget
  const totalBudget = projectCostCodes.reduce((sum, pcc) => {
    return sum + (pcc.budgetAmount || 0);
  }, 0);

  // Group by division for display
  const groupedCostCodes = projectCostCodes.reduce(
    (acc, pcc) => {
      const division = pcc.costCode.division;
      if (!acc[division]) {
        acc[division] = [];
      }
      acc[division].push(pcc);
      return acc;
    },
    {} as Record<string, typeof projectCostCodes>
  );

  // Sort divisions
  const sortedDivisions = Object.keys(groupedCostCodes).sort((a, b) => {
    const aNum = parseInt(a.split(" ")[0] || "0");
    const bNum = parseInt(b.split(" ")[0] || "0");
    return aNum - bNum;
  });

  if (isLoading) {
    return (
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Skeleton height={40} width={200} />
          <Skeleton height={400} radius="md" />
        </Stack>
      </Paper>
    );
  }

  const assignedCostCodeIds = projectCostCodes.map((pcc) => pcc.costCodeId);

  return (
    <>
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start">
            <Title order={2}>Cost Codes</Title>
            <Button leftSection={<IconPlus size={16} />} onClick={openSelector}>
              Add Cost Codes
            </Button>
          </Group>

            {projectCostCodes.length === 0 ? (
              <Paper p="xl" withBorder style={{ borderStyle: "dashed" }}>
                <Stack gap="md" align="center">
                  <Text c="dimmed">No cost codes assigned to this project yet.</Text>
                  <Button leftSection={<IconPlus size={16} />} onClick={openSelector}>
                    Add Your First Cost Code
                  </Button>
                </Stack>
              </Paper>
            ) : (
              <>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    {projectCostCodes.length} cost{" "}
                    {projectCostCodes.length === 1 ? "code" : "codes"} assigned
                  </Text>
                  <Text size="sm" fw={500}>
                    Total Budget: ${totalBudget.toLocaleString()}
                  </Text>
                </Group>

                <Stack gap="md">
                  {sortedDivisions.map((division) => (
                    <div key={division}>
                      <Badge variant="light" size="lg" mb="sm">
                        {division}
                      </Badge>
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Code</Table.Th>
                            <Table.Th>Name</Table.Th>
                            <Table.Th style={{ width: "200px" }}>Budget Amount</Table.Th>
                            <Table.Th style={{ width: "60px" }}></Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {(groupedCostCodes[division] || []).map((pcc) => (
                            <Table.Tr key={pcc.id}>
                              <Table.Td>
                                <Text size="sm" fw={500}>
                                  {pcc.costCode.code}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">{pcc.costCode.name}</Text>
                              </Table.Td>
                              <Table.Td>
                                {editingBudget === pcc.id ? (
                                  <NumberInput
                                    size="sm"
                                    prefix="$"
                                    thousandSeparator=","
                                    defaultValue={pcc.budgetAmount || 0}
                                    onBlur={(e) => {
                                      const target = e.currentTarget as HTMLInputElement;
                                      handleBudgetUpdate(pcc.id, target.value);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        const target = e.currentTarget as HTMLInputElement;
                                        handleBudgetUpdate(pcc.id, target.value);
                                      } else if (e.key === "Escape") {
                                        setEditingBudget(null);
                                      }
                                    }}
                                    autoFocus
                                  />
                                ) : (
                                  <Text
                                    size="sm"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setEditingBudget(pcc.id)}
                                  >
                                    {pcc.budgetAmount
                                      ? `$${pcc.budgetAmount.toLocaleString()}`
                                      : "-"}
                                  </Text>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <ActionIcon
                                  color="red"
                                  variant="subtle"
                                  onClick={() => handleRemove(pcc.id)}
                                  loading={removeProjectCostCode.isPending}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </div>
                  ))}
                </Stack>
              </>
            )}
          </Stack>
        </Paper>

      <CostCodeSelector
        opened={selectorOpened}
        onClose={closeSelector}
        onSelect={handleAddCostCodes}
        excludeIds={assignedCostCodeIds}
      />
    </>
  );
}
